/* =====================================================================================
   Council: an AI shadow board.

   A fixed set of expert "seats" (a Chair, a CFO, a General Counsel, an investor lens, plus
   whatever sector-specific seat a tenant adds) reviews a question independently, then the
   Chair reads every other seat's answer and synthesises a single recommendation. The result
   is stored as a minute (council.sessions/responses), the same way Chorus stores messages:
   an auditable record, not a chat log that scrolls away.

   WHERE THE MODEL CALL DOES NOT HAPPEN. Not on Forum's server, anywhere. Every call goes
   straight from this browser tab to the provider's own API, using a key the signed-in
   person pastes in once and that lives only in this browser's localStorage, never sent to
   Supabase, never logged, never billed to Forum. That is what "anthropic-dangerous-direct-
   browser-access" is for: a documented opt-in for exactly this bring-your-own-key pattern,
   not a hack. Anyone with devtools open can read their own key back out of their own
   requests, which is a fair trade for a key nobody but them ever held in the first place.

   PLUGGABLE PROVIDERS. council.responses.provider carries no check constraint by design.
   PROVIDERS below is the single place a second provider (OpenAI, Gemini, anything) gets
   added, as one more entry with a call() function and a settings key. Only Anthropic is
   wired up today because that is the key the founder is using; adding a provider never
   touches the schema, the seat model, or how a session is run.

   ANONYMISATION IS A TOGGLE, NOT A PROMISE. The real privacy backstop is that API traffic
   (as opposed to the consumer chat apps) is not used for training and is deleted on a short
   window by both providers' own policy. Redaction on top of that is a plain, transparent
   find-and-replace against a list the org maintains itself: good defence in depth, not a
   claim that freeform financial or legal text has been made anonymous.
   ===================================================================================== */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BACKEND } from '../lib/db';
import { TENANT } from '@tenant';
import { type Scenario, loadScenarios, loadChildren, summarizeForBoard } from '../lib/glidepath';
import './council.css';

interface Seat {
  id: string; name: string; persona: string;
  is_chair: boolean; sort_order: number; active: boolean;
}
interface Session {
  id: string; question: string; context: string | null; glidepath_snapshot: string | null;
  created_at: string; filed_at: string | null;
}
interface Resp {
  id: string; session_id: string; seat_id: string | null; seat_name: string;
  is_chair: boolean; provider: string; model: string; response: string; created_at: string;
}

/* ---------------- providers: the one pluggable seam ---------------- */

interface ProviderDef {
  label: string;
  keyStorageName: string;
  defaultModel: string;
  call: (apiKey: string, model: string, system: string, userText: string) => Promise<string>;
}

async function callAnthropic(apiKey: string, model: string, system: string, userText: string): Promise<string> {
  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      // Opus models reason before answering by default, not on request, and that reasoning
      // draws from the same max_tokens budget as the visible reply. At 2000 a seat given a
      // genuinely analytical question (review this plan, weigh this trade-off) could spend
      // every token thinking and stop at max_tokens having written nothing: a real seat's
      // response, silently empty, not a failure any error handling below would catch. 8000
      // leaves real room for both after a live test confirmed 2000 wasn't enough.
      model, max_tokens: 8000, system,
      messages: [{ role: 'user', content: userText }],
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error?.message || `Anthropic API returned ${res.status}`);
  }
  const block = (body.content ?? []).find((b: { type: string }) => b.type === 'text');
  if (block?.text) return block.text;
  if (body.stop_reason === 'max_tokens') {
    throw new Error('Ran out of budget mid-thought before writing an answer. Try a shorter question, or ask again.');
  }
  if (body.stop_reason === 'refusal') {
    throw new Error('The model declined to answer this one.');
  }
  return '(no response text)';
}

const PROVIDERS: Record<string, ProviderDef> = {
  anthropic: {
    label: 'Claude (Anthropic)',
    keyStorageName: 'council:key:anthropic',
    defaultModel: 'claude-opus-5',
    call: callAnthropic,
  },
  // A second provider is one more entry here, e.g. openai: { label: 'ChatGPT (OpenAI)',
  // keyStorageName: 'council:key:openai', defaultModel: 'gpt-5', call: callOpenAI }, plus
  // its own key field in the settings panel below. Nothing else in this file changes.
};

const getKey = (p: string) => localStorage.getItem(PROVIDERS[p]?.keyStorageName ?? '') || '';
const setKey = (p: string, v: string) => {
  const name = PROVIDERS[p]?.keyStorageName;
  if (!name) return;
  if (v) localStorage.setItem(name, v); else localStorage.removeItem(name);
};

/* ---------------- redaction: a plain find/replace the org controls ---------------- */

const REDACT_STORE = 'council:redact_terms';
const loadRedactTerms = (): string[] => {
  try { return JSON.parse(localStorage.getItem(REDACT_STORE) || '[]'); } catch { return []; }
};
const saveRedactTerms = (terms: string[]) => localStorage.setItem(REDACT_STORE, JSON.stringify(terms));

function redact(text: string, terms: string[]): { text: string; count: number } {
  let out = text, count = 0;
  terms.filter(Boolean).forEach((term, i) => {
    const re = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    out = out.replace(re, () => { count++; return `[REDACTED-${i + 1}]`; });
  });
  return { text: out, count };
}

/* ---------------- component ---------------- */

export function CouncilView() {
  const [tab, setTab] = useState<'convene' | 'board'>('convene');
  const [seats, setSeats] = useState<Seat[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [responsesBySession, setResponsesBySession] = useState<Record<string, Resp[]>>({});
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');
  const [anonymize, setAnonymize] = useState(true);
  const [redactTerms, setRedactTerms] = useState<string[]>(loadRedactTerms());
  const [newTerm, setNewTerm] = useState('');
  const [running, setRunning] = useState<string | null>(null); // seat name currently answering, or the chair
  const [editingSession, setEditingSession] = useState<string | null>(null); // session id being edited, if any
  const questionRef = useRef<HTMLTextAreaElement | null>(null);

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [includeGlidepath, setIncludeGlidepath] = useState(true);
  const glidepathScenario = useMemo(
    () => scenarios.find((s) => s.case_type === 'base') ?? scenarios[0] ?? null,
    [scenarios],
  );

  const [anthropicKey, setAnthropicKey] = useState(getKey('anthropic'));
  const [keyDraft, setKeyDraft] = useState('');

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  const loadSeats = useCallback(async () => {
    if (BACKEND !== 'supabase') { setSeats([]); return; }
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase().schema('council').from('seats')
      .select('*').order('sort_order');
    if (error) { flash(error.message); return; }
    setSeats((data as Seat[]) ?? []);
  }, []);

  const loadSessions = useCallback(async () => {
    if (BACKEND !== 'supabase') { setSessions([]); return; }
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase().schema('council').from('sessions')
      .select('*').order('created_at', { ascending: false }).limit(50);
    if (error) { flash(error.message); return; }
    setSessions((data as Session[]) ?? []);
  }, []);

  const loadResponses = useCallback(async (sessionId: string) => {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase().schema('council').from('responses')
      .select('*').eq('session_id', sessionId).order('is_chair').order('created_at');
    if (error) { flash(error.message); return; }
    setResponsesBySession((m) => ({ ...m, [sessionId]: (data as Resp[]) ?? [] }));
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [, , scen] = await Promise.all([loadSeats(), loadSessions(), loadScenarios().catch(() => [])]);
      setScenarios(scen ?? []);
      setLoading(false);
    })();
  }, [loadSeats, loadSessions]);

  const activeSeats = useMemo(() => seats.filter((s) => s.active), [seats]);
  const chairSeat = useMemo(() => activeSeats.find((s) => s.is_chair) ?? null, [activeSeats]);
  const panelSeats = useMemo(() => activeSeats.filter((s) => !s.is_chair), [activeSeats]);

  /* ---------------- seat CRUD ---------------- */

  async function addSeat() {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase().schema('council').from('seats').insert({
      name: 'New seat', persona: 'Describe this seat’s role and what it should focus on.',
      sort_order: (seats.at(-1)?.sort_order ?? -1) + 1,
    }).select('*').single();
    if (error) { flash(error.message); return; }
    setSeats((s) => [...s, data as Seat]);
  }
  async function patchSeat(id: string, patch: Partial<Seat>) {
    setSeats((s) => s.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('council').from('seats').update(patch).eq('id', id);
    if (error) flash(error.message);
  }
  async function deleteSeat(id: string) {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('council').from('seats').delete().eq('id', id);
    if (error) { flash(error.message); return; }
    setSeats((s) => s.filter((x) => x.id !== id));
  }
  function moveSeat(id: string, dir: -1 | 1) {
    const idx = seats.findIndex((s) => s.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= seats.length) return;
    const a = seats[idx], b = seats[j];
    patchSeat(a.id, { sort_order: b.sort_order });
    patchSeat(b.id, { sort_order: a.sort_order });
  }

  /* ---------------- redaction term list ---------------- */

  function addTerm() {
    const t = newTerm.trim();
    if (!t) return;
    const next = [...redactTerms, t];
    setRedactTerms(next); saveRedactTerms(next); setNewTerm('');
  }
  function removeTerm(t: string) {
    const next = redactTerms.filter((x) => x !== t);
    setRedactTerms(next); saveRedactTerms(next);
  }

  /* ---------------- convene the board ---------------- */

  // Shared by the "Convene the board" button and every session's "Rerun": both just ask
  // the same question, either freshly typed or copied back out of a past session (useful
  // when a seat failed to reach the model, or seat personas have changed since). A rerun
  // is a NEW session row, not an edit of the old one, so a failed attempt stays on the
  // record rather than being overwritten by the retry that fixed it. The Glidepath
  // snapshot, when included, is fetched fresh here every time rather than replayed from a
  // past session: a rerun should see this week's numbers, not a stale copy of last week's.
  async function runBoard(q: string, ctx: string): Promise<boolean> {
    if (!q.trim()) { flash('Enter a question for the board.'); return false; }
    if (!anthropicKey) { flash('Add your Anthropic API key in Settings below first.'); return false; }
    if (panelSeats.length === 0) { flash('No active seats to ask: add or activate seats on the Board tab.'); return false; }

    const provider = PROVIDERS.anthropic;

    let snapshot: string | null = null;
    if (includeGlidepath && glidepathScenario) {
      setRunning('Reading Glidepath');
      try {
        const { channels, opex, equity } = await loadChildren(glidepathScenario.id);
        snapshot = summarizeForBoard(glidepathScenario, channels, opex, equity);
      } catch (e: any) {
        flash(`Could not read Glidepath: ${e.message}`);
      }
    }

    const parts = [q.trim()];
    if (ctx.trim()) parts.push(`Context:\n${ctx.trim()}`);
    if (snapshot) parts.push(snapshot);
    const rawText = parts.join('\n\n');
    const { text: sendText, count } = anonymize ? redact(rawText, redactTerms) : { text: rawText, count: 0 };

    setRunning('Convening the board');
    try {
      const { supabase } = await import('../lib/supabase');
      const db = supabase().schema('council');

      const { data: sessionRow, error: sErr } = await db.from('sessions')
        .insert({ question: q.trim(), context: ctx.trim() || null, glidepath_snapshot: snapshot }).select('*').single();
      if (sErr || !sessionRow) throw new Error(sErr?.message ?? 'Could not create the session.');
      const session = sessionRow as Session;

      const panelResults = await Promise.all(panelSeats.map(async (seat) => {
        setRunning(seat.name);
        try {
          const text = await provider.call(anthropicKey, provider.defaultModel, seat.persona, sendText);
          return { seat, text, ok: true as const };
        } catch (e: any) {
          return { seat, text: `This seat could not be reached: ${e.message}`, ok: false as const };
        }
      }));

      const rows = panelResults.map((r) => ({
        session_id: session.id, seat_id: r.seat.id, seat_name: r.seat.name, is_chair: false,
        provider: 'anthropic', model: provider.defaultModel, response: r.text,
      }));

      if (chairSeat) {
        setRunning(chairSeat.name);
        const digest = panelResults.map((r) => `${r.seat.name}:\n${r.text}`).join('\n\n---\n\n');
        const chairPrompt = `Question put to the board:\n${sendText}\n\nEach seat's answer, in order:\n\n${digest}`;
        try {
          const text = await provider.call(anthropicKey, provider.defaultModel, chairSeat.persona, chairPrompt);
          rows.push({
            session_id: session.id, seat_id: chairSeat.id, seat_name: chairSeat.name, is_chair: true,
            provider: 'anthropic', model: provider.defaultModel, response: text,
          });
        } catch (e: any) {
          rows.push({
            session_id: session.id, seat_id: chairSeat.id, seat_name: chairSeat.name, is_chair: true,
            provider: 'anthropic', model: provider.defaultModel,
            response: `The chair could not be reached: ${e.message}`,
          });
        }
      }

      const { error: rErr } = await db.from('responses').insert(rows);
      if (rErr) throw new Error(rErr.message);

      setResponsesBySession((m) => ({ ...m, [session.id]: rows as unknown as Resp[] }));
      setSessions((s) => [session, ...s]);
      setOpenSession(session.id);
      await loadResponses(session.id);
      flash(anonymize && count > 0 ? `Convened. ${count} term${count === 1 ? '' : 's'} redacted before sending.` : 'Convened.');
      return true;
    } catch (e: any) {
      flash(e.message);
      return false;
    } finally {
      setRunning(null);
    }
  }

  async function convene() {
    if (await runBoard(question, context)) { setQuestion(''); setContext(''); setEditingSession(null); }
  }

  async function rerunSession(s: Session) {
    await runBoard(s.question, s.context ?? '');
  }

  // Loads a past session's question and context into the form above so they can be
  // changed before asking again, rather than only ever re-asking verbatim. Deliberately
  // leaves the Glidepath toggle as it currently stands: the snapshot is always regenerated
  // fresh on submit anyway (see runBoard), so there is nothing stale to carry over here.
  function editSession(s: Session) {
    setQuestion(s.question);
    setContext(s.context ?? '');
    setEditingSession(s.id);
    setTab('convene');
    questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    questionRef.current?.focus();
  }

  // Filing marks a session the settled record of its question, the same draft/approved
  // distinction Canon makes for a policy: a filed session drops Edit, Rerun and Delete from
  // its own row, so "the board's answer on this" stays pointable-to rather than quietly
  // rewritable. Unfiling undoes that for a genuine mistake; it is one click on purpose,
  // this being a founder's own advisory record rather than a statutory filing.
  async function fileSession(s: Session, filed: boolean) {
    const filed_at = filed ? new Date().toISOString() : null;
    setSessions((list) => list.map((x) => (x.id === s.id ? { ...x, filed_at } : x)));
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('council').from('sessions').update({ filed_at }).eq('id', s.id);
    if (error) { flash(error.message); setSessions((list) => list.map((x) => (x.id === s.id ? { ...x, filed_at: s.filed_at } : x))); return; }
    flash(filed ? 'Filed as the settled record.' : 'Unfiled.');
  }

  async function deleteSession(s: Session) {
    if (!confirm(`Delete this session ("${s.question}")? This cannot be undone.`)) return;
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('council').from('sessions').delete().eq('id', s.id);
    if (error) { flash(error.message); return; }
    setSessions((list) => list.filter((x) => x.id !== s.id));
    setResponsesBySession((m) => { const { [s.id]: _drop, ...rest } = m; return rest; });
    if (openSession === s.id) setOpenSession(null);
    flash('Session deleted.');
  }

  function saveKey() {
    setKey('anthropic', keyDraft.trim());
    setAnthropicKey(keyDraft.trim());
    setKeyDraft('');
    flash(keyDraft.trim() ? 'Key saved in this browser only.' : 'Key removed.');
  }

  if (BACKEND !== 'supabase') {
    return (
      <div className="page">
        <div className="page-head">
          <h1 className="page-title">Council</h1>
          <p className="page-sub">The shadow board needs a live backend: not available in this preview.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Council</h1>
        <p className="page-sub">
          An AI shadow board. Each active seat below answers your question independently, in its
          own voice; the Chair reads every answer and gives you one recommendation. Every call
          runs from this browser using your own key, so {TENANT.brand.orgName} never sees it and
          is never billed for it.
        </p>
      </div>

      <div className="toolbar">
        <div className="seg">
          <button className={tab === 'convene' ? 'on' : ''} onClick={() => setTab('convene')}>Convene</button>
          <button className={tab === 'board' ? 'on' : ''} onClick={() => setTab('board')}>Board ({seats.length})</button>
        </div>
      </div>

      {msg && <div className="council-flash">{msg}</div>}
      {loading && <p className="loading">Loading...</p>}

      {!loading && tab === 'board' && (
        <div className="council-board">
          {seats.map((seat, i) => (
            <div key={seat.id} className={`card council-seat ${!seat.active ? 'inactive' : ''}`}>
              <div className="council-seat-head">
                <input className="input council-seat-name" value={seat.name}
                       onChange={(e) => patchSeat(seat.id, { name: e.target.value })} />
                <div className="council-seat-actions">
                  <button className="btn sm ghost" disabled={i === 0} onClick={() => moveSeat(seat.id, -1)}>Up</button>
                  <button className="btn sm ghost" disabled={i === seats.length - 1} onClick={() => moveSeat(seat.id, 1)}>Down</button>
                  <label className="council-chair-toggle">
                    <input type="checkbox" checked={seat.is_chair}
                           onChange={(e) => patchSeat(seat.id, { is_chair: e.target.checked })} />
                    Chair
                  </label>
                  <label className="council-chair-toggle">
                    <input type="checkbox" checked={seat.active}
                           onChange={(e) => patchSeat(seat.id, { active: e.target.checked })} />
                    Active
                  </label>
                  <button className="btn sm ghost" onClick={() => deleteSeat(seat.id)}>Remove</button>
                </div>
              </div>
              <textarea className="input council-persona" rows={3} value={seat.persona}
                        onChange={(e) => patchSeat(seat.id, { persona: e.target.value })} />
            </div>
          ))}
          <button className="btn" onClick={addSeat}>+ Add seat</button>

          <div className="divider" style={{ margin: '20px 0' }} />
          <div className="card council-settings">
            <h2 style={{ marginTop: 0 }}>Settings</h2>
            <div className="label">Anthropic API key (this browser only)</div>
            <div className="council-key-row">
              <input className="input" type="password" placeholder={anthropicKey ? 'Key saved. Enter a new one to replace it.' : 'sk-ant-...'}
                     value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)} />
              <button className="btn" onClick={saveKey}>{anthropicKey ? 'Replace' : 'Save'}</button>
              {anthropicKey && <button className="btn ghost" onClick={() => { setKey('anthropic', ''); setAnthropicKey(''); }}>Clear</button>}
            </div>
            <p className="council-hint">
              Stored in localStorage on this device only. Never sent to Forum's server, never
              committed anywhere. Get a key at console.anthropic.com, under API Keys.
            </p>

            <div className="label" style={{ marginTop: 16 }}>Redaction terms</div>
            <p className="council-hint">
              Anything listed here is replaced with a placeholder before your question is sent,
              when "Anonymise before sending" is on: company name, real names, exact figures,
              whatever you'd rather not put in front of a model, even one that doesn't train on it.
            </p>
            <div className="council-key-row">
              <input className="input" placeholder={`e.g. ${TENANT.brand.orgName}, a figure, a person's name`}
                     value={newTerm} onChange={(e) => setNewTerm(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && addTerm()} />
              <button className="btn" onClick={addTerm}>Add</button>
            </div>
            <div className="council-terms">
              {redactTerms.map((t) => (
                <span key={t} className="pill mute council-term">
                  {t} <button onClick={() => removeTerm(t)}>x</button>
                </span>
              ))}
              {redactTerms.length === 0 && <span className="council-hint">No terms yet.</span>}
            </div>
          </div>
        </div>
      )}

      {!loading && tab === 'convene' && (
        <div className="council-convene">
          <div className="card">
            {editingSession && (
              <div className="council-editing-banner">
                <span>Editing a past question. Change it below, then Convene to ask it again as a new session.</span>
                <button className="btn sm ghost" onClick={() => { setEditingSession(null); setQuestion(''); setContext(''); }}>Cancel</button>
              </div>
            )}
            <div className="label">Question for the board</div>
            <textarea ref={questionRef} className="input" rows={3} placeholder="e.g. Should we take the term sheet as offered, or push back on the board seat?"
                      value={question} onChange={(e) => setQuestion(e.target.value)} />
            <div className="label" style={{ marginTop: 10 }}>Context (optional)</div>
            <textarea className="input" rows={4} placeholder="Paste in whatever the board needs to see: numbers, terms, a document excerpt."
                      value={context} onChange={(e) => setContext(e.target.value)} />
            {glidepathScenario && (
              <label className="council-chair-toggle" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={includeGlidepath} onChange={(e) => setIncludeGlidepath(e.target.checked)} />
                Include Glidepath data ("{glidepathScenario.name}", regenerated fresh each time)
              </label>
            )}
            <div className="council-run-row">
              <label className="council-chair-toggle">
                <input type="checkbox" checked={anonymize} onChange={(e) => setAnonymize(e.target.checked)} />
                Anonymise before sending ({redactTerms.length} term{redactTerms.length === 1 ? '' : 's'})
              </label>
              <button className="btn primary" onClick={convene} disabled={!!running}>
                {running ? `${running}...` : `Convene the board (${panelSeats.length} seat${panelSeats.length === 1 ? '' : 's'}${chairSeat ? ' + Chair' : ''})`}
              </button>
            </div>
            {!anthropicKey && <p className="council-hint council-warn">No Anthropic key saved yet: add one on the Board tab under Settings.</p>}
            {!chairSeat && <p className="council-hint council-warn">No seat is marked Chair: the session will run without a synthesis.</p>}
          </div>

          <div className="divider" style={{ margin: '20px 0' }} />

          {sessions.length === 0 && <p className="module-empty">No sessions yet. Ask the board something above.</p>}
          {sessions.map((s) => {
            const open = openSession === s.id;
            const resp = responsesBySession[s.id];
            const filed = !!s.filed_at;
            return (
              <div key={s.id} className={`card council-session ${filed ? 'filed' : ''}`}>
                <div className="council-session-head">
                  <button className="council-session-toggle" onClick={async () => {
                    const next = open ? null : s.id;
                    setOpenSession(next);
                    if (next && !responsesBySession[s.id]) await loadResponses(s.id);
                  }}>
                    <span className="council-session-q">{s.question}</span>
                    <span className="council-hint">{new Date(s.created_at).toLocaleString('en-GB')}</span>
                  </button>
                  {filed ? (
                    <>
                      <span className="pill ok council-filed-pill" title={`Filed ${new Date(s.filed_at!).toLocaleString('en-GB')}`}>Filed</span>
                      <button className="btn sm ghost" onClick={() => fileSession(s, false)}>Unfile</button>
                    </>
                  ) : (
                    <>
                      <button className="btn sm ghost" disabled={!!running}
                              onClick={() => editSession(s)} title="Load this question back into the form above, to change it before asking again">
                        Edit
                      </button>
                      <button className="btn sm ghost council-rerun" disabled={!!running}
                              onClick={() => rerunSession(s)} title="Ask the board this same question again, unchanged, as a new session">
                        {running ? '...' : 'Rerun'}
                      </button>
                      <button className="btn sm ghost" onClick={() => fileSession(s, true)}
                              title="Mark this the settled record of this question; locks it from further edits">
                        File
                      </button>
                      <button className="btn sm ghost council-delete" onClick={() => deleteSession(s)}>Delete</button>
                    </>
                  )}
                </div>
                {open && (
                  <div className="council-transcript">
                    {s.context && <div className="council-context"><strong>Context:</strong> {s.context}</div>}
                    {s.glidepath_snapshot && (
                      <details className="council-context">
                        <summary><strong>Glidepath data reviewed</strong></summary>
                        <div className="council-snapshot">{s.glidepath_snapshot}</div>
                      </details>
                    )}
                    {!resp && <p className="loading">Loading...</p>}
                    {resp?.filter((r) => !r.is_chair).map((r) => (
                      <div key={r.id} className="council-response">
                        <div className="council-response-seat">{r.seat_name}</div>
                        <div className="council-response-text">{r.response}</div>
                      </div>
                    ))}
                    {resp?.filter((r) => r.is_chair).map((r) => (
                      <div key={r.id} className="council-response council-chair-response">
                        <div className="council-response-seat">{r.seat_name}, synthesis</div>
                        <div className="council-response-text">{r.response}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
